import { Controller } from "@hotwired/stimulus"

// Editable dropdown behind the "Firma" and "Prevádzka" fields of the inspection
// form: it filters records live, keeps free text for records that do not exist
// yet and pins a "+ New …" escape hatch to the bottom of the list. Subclasses
// describe the rows and decide what a pick fills in.
export default class Combobox extends Controller {
  static targets = ["input", "hidden", "list", "newOption", "arrow", "clearBtn", "hint"]
  static values = { url: String, newHint: String, existingHint: String }

  connect() {
    this.resetPaging()
    this.hideOnOutsideClick = (event) => { if (!this.element.contains(event.target)) this.closeList() }
    document.addEventListener("click", this.hideOnOutsideClick)
    this.refresh()
  }

  disconnect() {
    document.removeEventListener("click", this.hideOnOutsideClick)
    clearTimeout(this.debounceTimer)
  }

  open() {
    if (this.skipOpen || this.isOpen || this.loading) return

    this.resetPaging()
    this.clearOptions()
    this.fetchPage()
  }

  input() {
    this.detach()
    this.refresh()

    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.resetPaging()
      this.clearOptions()
      this.fetchPage()
    }, 150)
  }

  // "+ New …" only hands the field back to the technician — whatever was typed
  // stays, and the ✕ button next to it takes them back to the list.
  startNew() {
    this.detach()
    this.closeList()
    this.refresh()
    this.focusInput()
  }

  clearInput() {
    this.inputTarget.value = ""
    this.clear()
    this.refresh()
    this.focusInput()
    this.open()
  }

  navigate(event) {
    const options = this.optionElements
    if (options.length === 0 && event.key !== "Escape") return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.highlight(Math.min(this.highlighted + 1, options.length - 1))
      if (this.highlighted >= options.length - 3) this.fetchPage()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.highlight(Math.max(this.highlighted - 1, -1))
    } else if (event.key === "Enter" && this.highlighted >= 0) {
      event.preventDefault()
      this.select(JSON.parse(options[this.highlighted].dataset.item))
    } else if (event.key === "Escape") {
      this.closeList()
    }
  }

  scroll() {
    const list = this.listTarget
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 80) this.fetchPage()
  }

  async fetchPage() {
    if (this.loading || !this.hasMore) return
    this.loading = true

    const query = this.inputTarget.value.trim()
    const url = new URL(this.urlValue, window.location.origin)
    if (query) url.searchParams.set("q[name_cont]", query)
    url.searchParams.set("page", this.page)
    this.appendParams(url)

    const generation = this.generation
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    const { results, has_more } = await response.json()

    // The list was reset or closed while this page was in flight.
    if (generation !== this.generation) return

    this.loading = false
    this.hasMore = has_more

    // Nothing stored at all yet — the field is a plain input, without a dropdown.
    if (this.page === 1 && results.length === 0 && !query) {
      this.closeList()
      return
    }

    results.forEach((item) => this.listTarget.insertBefore(this.buildOption(item), this.newOptionTarget))
    this.page++
    this.listTarget.classList.remove("hidden")
    this.positionList()
  }

  select(item) {
    this.inputTarget.value = item.name || ""
    this.applySelection(item)
    this.refresh()
    this.closeList()
  }

  refresh() {
    const filled = this.inputTarget.value.trim() !== ""
    this.arrowTarget.classList.toggle("hidden", filled)
    this.clearBtnTarget.classList.toggle("hidden", !filled)
    this.hintTarget.textContent = this.hint
  }

  buildOption(item) {
    const option = document.createElement("li")
    option.dataset.role = "option"
    option.dataset.item = JSON.stringify(item)
    option.className = "px-4 py-2 cursor-pointer hover:bg-gray-50"
    option.appendChild(this.line(this.optionTitle(item), "text-sm text-[#1c1b1f]"))

    const subtitle = this.optionSubtitle(item)
    if (subtitle) option.appendChild(this.line(subtitle, "text-xs text-[#49454f]"))

    option.addEventListener("mousedown", (event) => {
      event.preventDefault()
      this.select(item)
    })

    return option
  }

  line(text, className) {
    const element = document.createElement("div")
    element.className = className
    element.textContent = text
    return element
  }

  highlight(index) {
    this.highlighted = index
    this.optionElements.forEach((option, i) => option.classList.toggle("bg-gray-100", i === index))
    if (index >= 0) this.optionElements[index].scrollIntoView({ block: "nearest" })
  }

  positionList() {
    const list = this.listTarget
    const above = window.innerHeight - this.inputTarget.getBoundingClientRect().bottom < list.offsetHeight

    list.classList.toggle("bottom-full", above)
    list.classList.toggle("mb-1", above)
    list.classList.toggle("top-full", !above)
    list.classList.toggle("mt-1", !above)
  }

  closeList() {
    clearTimeout(this.debounceTimer)
    this.listTarget.classList.add("hidden")
    this.clearOptions()
    this.resetPaging()
  }

  clearOptions() {
    this.optionElements.forEach((option) => option.remove())
  }

  resetPaging() {
    this.generation = (this.generation || 0) + 1
    this.page = 1
    this.hasMore = true
    this.loading = false
    this.highlighted = -1
  }

  // Focusing would re-open the list we just closed, so suppress that one event.
  focusInput() {
    this.skipOpen = true
    this.inputTarget.focus()
    this.skipOpen = false
  }

  get optionElements() {
    return Array.from(this.listTarget.querySelectorAll('[data-role="option"]'))
  }

  get isOpen() {
    return !this.listTarget.classList.contains("hidden")
  }

  get hint() {
    if (this.inputTarget.value.trim() === "") return ""

    return this.selected ? this.existingHintValue : this.newHintValue
  }

  get selected() {
    return this.hiddenTarget.value !== ""
  }

  optionTitle(item) {
    return item.name
  }

  optionSubtitle(_item) {
    return null
  }

  appendParams(_url) {
  }

  applySelection(_item) {
  }

  // Typing drops the link to the stored record but keeps what is on screen —
  // it is a fine starting point for the record about to be created.
  detach() {
  }

  // The ✕ additionally blanks everything that came from that record.
  clear() {
    this.detach()
  }
}
